import { CheckIcon } from '@src/components/icons/CheckIcon';
import { Panel } from '@src/components/Panel/Panel';
import type { ReactNode } from 'react';
import { Badge } from '@src/components/Badge/Badge';
import { Button, type ButtonVariant } from '@src/components/Button/Button';
import { testIdAttr } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './DistributionMethodCard.css';

export type DistributionMethodTone = 'accent' | 'info' | 'success' | 'support' | 'warning';

export interface DistributionMethodCardProps {
  surface?: 'card' | 'plain' | 'outlined';
  icon: ReactNode;
  tone: DistributionMethodTone;
  title: string;
  body: string;
  points?: Array<string | { text: string; neutral: boolean }>;
  badge?: string;
  highlight?: boolean;
  hint?: string;
  help?: ReactNode;
  onClick?: () => void;
  action?: { label: string; onClick: () => void; disabled?: boolean; variant?: ButtonVariant; icon?: ReactNode };
  testId?: string;
}

/** One way to hand out draw results — icon, copy, optional CTA or scroll hint. */
export function DistributionMethodCard({
  surface = 'card',
  icon,
  tone,
  title,
  body,
  points,
  badge,
  highlight = false,
  hint,
  help,
  onClick,
  action,
  testId,
}: DistributionMethodCardProps) {
  const clickable = Boolean(onClick);
  const classes = ['distribution-method'];
  if (surface === 'outlined') classes.push('distribution-method--outlined');
  if (highlight) {
    classes.push('distribution-method--highlight');
  }
  if (clickable) {
    classes.push('distribution-method--clickable');
  }

  const content = (
    <article
      className={classes.join(' ')}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      {...testIdAttr(testId)}
    >
      <div className="distribution-method__head">
        <span className={`distribution-method__icon distribution-method__icon--${tone}`} aria-hidden>
          {icon}
        </span>
        <div className="distribution-method__title-row">
          <Typography as="h3" role="title/card">
            {title}
          </Typography>
          {badge ? (
            <Badge
              tone={
                tone === 'success' || tone === 'info'
                  ? 'success'
                  : tone === 'accent' || tone === 'warning'
                    ? 'warning'
                    : 'info'
              }
            >
              {badge}
            </Badge>
          ) : null}
        </div>
      </div>
      <Typography as="p" role="body/sm" className="distribution-method__body">
        {body}
      </Typography>
      {points?.length ? (
        <ul className="distribution-method__points">
          {points.map(point => {
            const text = typeof point === 'string' ? point : point.text;
            const neutral = typeof point !== 'string' && point.neutral;
            return <li key={text}>
              {neutral ? <span className="distribution-method__neutral" aria-hidden="true">–</span> : <CheckIcon aria-hidden="true" />}
              <Typography role="body/sm">{text}</Typography>
            </li>;
          })}
        </ul>
      ) : null}
      {hint || action || help ? (
        <div className="distribution-method__footer">
          {hint ? (
            <Typography as="p" role="meta/helper" className="distribution-method__hint">
              {hint}
            </Typography>
          ) : null}
          {action ? (
            <Button
              variant={action.variant ?? 'secondary'}
              icon={action.icon}
              size="large"
              fullWidth
              disabled={action.disabled}
              onClick={action.onClick}
              testId={testId ? `${testId}-action` : undefined}
            >
              {action.label}
            </Button>
          ) : null}
          {help}
        </div>
      ) : null}
    </article>
  );
  return surface !== 'card' ? content : (
    <Panel edges={{ pieces: tone === 'warning' ? 5 : tone === 'info' ? 4 : 2 }} elevated padding="none" className="distribution-method-panel">
      {content}
    </Panel>
  );
}
